import React from 'react';
import { motion } from "framer-motion";
import { Lock, Server, Code, Bug, Eye, Database, Fingerprint } from "lucide-react";
import { Card, CardContent } from "@/shared/components/ui/card";

const Security = () => {
  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Stream */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
            <Lock className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-slate-400">Security Architecture</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Engineered for <br />
            <span className="gradient-text">Zero Compromise</span>
          </h1>
          <p className="text-lg text-slate-400 leading-relaxed">
            Luxkgate processes billions of dollars in volume. To protect our merchants, we deploy military-grade encryption, continuous penetration testing, and zero-trust infrastructure.
          </p>
        </motion.div>

        {/* Feature Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {[
            {
              icon: Database,
              title: "Vault Tokenization",
              description: "Raw credit card data never touches your servers. We generate non-reversible proxy tokens for every card ingested which are then vaulted in isolated environments.",
              color: "text-blue-400",
              bgColor: "bg-blue-500/10"
            },
            {
              icon: Server,
              title: "Zero-Trust Network",
              description: "Our infrastructure relies on mutual TLS (mTLS) for all internal communication. Services only communicate with cryptographic verification.",
              color: "text-emerald-400",
              bgColor: "bg-emerald-500/10"
            },
            {
              icon: Fingerprint,
              title: "Two-Factor Auth (2FA)",
              description: "Access to dashboards requires robust hardware-backed MFA (WebAuthn / YubiKey) or TOTP to heavily restrict account takeovers.",
              color: "text-violet-400",
              bgColor: "bg-violet-500/10"
            },
            {
              icon: Code,
              title: "Static & Dynamic Analysis",
              description: "Our pipelines automatically run SAST and DAST analysis on every pull request, catching vulnerabilities before they ever deploy to production.",
              color: "text-amber-400",
              bgColor: "bg-amber-500/10"
            },
            {
              icon: Eye,
              title: "24/7 Threat Monitoring",
              description: "Dedicated SOC teams monitor anomaly detection networks around the clock, utilizing automated SIEM heuristics to isolate brute-force attacks.",
              color: "text-rose-400",
              bgColor: "bg-rose-500/10"
            },
            {
              icon: Bug,
              title: "Bug Bounty Program",
              description: "We work actively with elite cybersecurity researchers on HackerOne to crowdsource offensive audits of our public endpoints.",
              color: "text-cyan-400",
              bgColor: "bg-cyan-500/10"
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <Card className="bg-slate-900/40 border-slate-800/50 hover:border-slate-700 transition-colors h-full group">
                <CardContent className="p-8">
                  <div className={`w-12 h-12 rounded-lg ${item.bgColor} border border-slate-800/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <item.icon className={`h-6 w-6 ${item.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Responsible Disclosure */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card className="bg-slate-900/80 border-slate-800 overflow-hidden relative">
            <div className="absolute top-0 right-0 p-32 bg-blue-500/5 blur-[100px] rounded-full pointer-events-none" />
            <div className="lg:flex">
               <div className="lg:w-1/3 p-10 bg-slate-950/50 border-r border-slate-800 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold text-white mb-4">Report an Issue</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-6">
                    If you believe you have discovered a vulnerability within our API or Applications, please submit a report to our security team immediately.
                  </p>
               </div>
               <div className="lg:w-2/3 p-10 flex flex-col justify-center">
                 <h4 className="text-white font-semibold mb-2">PGP Encryption</h4>
                 <p className="text-slate-400 text-sm mb-6">For highly sensitive reports, please encrypt your payload using our public PGP key.</p>
                 <div className="bg-slate-950 font-mono text-sm text-emerald-400 p-6 rounded-lg border border-slate-800 overflow-x-auto whitespace-pre">
{`-----BEGIN PGP PUBLIC KEY BLOCK-----
Version: OpenPGP.js v4.10.4

mQINBGAx2U0BEAC9KHzZpB+s3Qd/p1B...
-----END PGP PUBLIC KEY BLOCK-----`}
                 </div>
                 <div className="mt-8">
                   <a href="mailto:security@luxkgate.com" className="inline-flex items-center text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors">
                     security@luxkgate.com <span className="ml-2">→</span>
                   </a>
                 </div>
               </div>
            </div>
          </Card>
        </motion.div>

      </div>
    </div>
  )
}

export default Security;